import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateFAQ, useUpdateFAQ, FAQ } from "@/hooks/use-faqs";
import { useAllFAQSections } from "@/hooks/use-faq-sections";

interface FAQModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  faq?: FAQ | null;
  defaultSectionId?: string;
}

export function FAQModal({ open, onOpenChange, faq, defaultSectionId }: FAQModalProps) {
  const [sectionId, setSectionId] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  const { data: sections } = useAllFAQSections();
  const createMutation = useCreateFAQ();
  const updateMutation = useUpdateFAQ();

  const isEditing = !!faq;

  useEffect(() => {
    if (faq) {
      setSectionId(faq.section_id);
      setQuestion(faq.question);
      setAnswer(faq.answer);
      setSortOrder(faq.sort_order);
      setIsActive(faq.is_active);
    } else {
      setSectionId(defaultSectionId || "");
      setQuestion("");
      setAnswer("");
      setSortOrder(0);
      setIsActive(true);
    }
  }, [faq, defaultSectionId, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim() || !answer.trim() || !sectionId) return;

    if (isEditing && faq) {
      updateMutation.mutate(
        {
          id: faq.id,
          section_id: sectionId,
          question: question.trim(),
          answer: answer.trim(),
          sort_order: sortOrder,
          is_active: isActive,
        },
        {
          onSuccess: () => onOpenChange(false),
        }
      );
    } else {
      createMutation.mutate(
        {
          section_id: sectionId,
          question: question.trim(),
          answer: answer.trim(),
          sort_order: sortOrder,
          is_active: isActive,
        },
        {
          onSuccess: () => onOpenChange(false),
        }
      );
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const isValid = question.trim() && answer.trim() && sectionId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="section">Section *</Label>
            <Select value={sectionId} onValueChange={setSectionId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a section" />
              </SelectTrigger>
              <SelectContent>
                {sections?.map((section) => (
                  <SelectItem key={section.id} value={section.id}>
                    {section.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(!sections || sections.length === 0) && (
              <p className="text-xs text-muted-foreground">
                You need to create a section first before adding FAQs.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="question">Question *</Label>
            <Input
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g., How do I reset my password?"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="answer">Answer *</Label>
            <Textarea
              id="answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Enter the answer to this question..."
              rows={5}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sortOrder">Sort Order</Label>
            <Input
              id="sortOrder"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">
              Lower numbers appear first within the section
            </p>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">Active</Label>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !isValid}>
              {isLoading ? "Saving..." : isEditing ? "Update FAQ" : "Add FAQ"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
